export {
  adjustToLocalTimezone,
  formatDateTime,
  localDateISO,
  maskDate,
  maskDateTime,
  maskISODate,
} from "@/utils/dates";

import {
  adjustToLocalTimezone,
  formatDateTime,
  localDateISO,
  maskDate,
  maskDateTime,
  maskISODate,
} from "@/utils/dates";

/**
 * Resets the properties of an object to their default values.
 *
 * @param targetObject - The object whose properties will be reset.
 * @param excludedProperties - An array of property names to ignore during the reset.
 * @returns The object with its properties reset.
 */
export function resetProperties(
  targetObject: Record<string, any>,
  excludedProperties: string[] = [],
): Record<string, any> {
  const defaultValue = (value: any): any => {
    switch (typeof value) {
      case "string":
        return "";
      case "number":
        return 0;
      case "boolean":
        return false;
      case "object":
        if (Array.isArray(value)) {
          return [];
        } else if (value === null) {
          return null;
        } else {
          return {};
        }
      default:
        return value;
    }
  };

  for (const property of Object.keys(targetObject)) {
    if (!excludedProperties.includes(property)) {
      targetObject[property] = defaultValue(targetObject[property]);
    }
  }
  return targetObject;
}

/**
 * Formats a number or string as Brazilian currency (BRL).
 *
 * @param value - The number or string to be formatted as currency.
 * @returns The formatted currency string.
 */
export function formatCurrency(value: number | string): string {
  let numericValue = parseFloat(value as string);
  if (isNaN(numericValue)) {
    numericValue = Number(value);
  }
  if (isNaN(numericValue)) {
    numericValue = 0;
  }
  return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(numericValue);
}

/**
 * Formats a number or string as Brazilian currency (BRL) with 4 decimal places.
 *
 * @param value - The number or string to be formatted as currency.
 * @returns The formatted currency string with 4 decimal places.
 */
export function formatCurrency4Decimals(value: number | string): string {
  let numericValue = parseFloat(value as string);
  if (isNaN(numericValue)) {
    numericValue = Number(value);
  }
  if (isNaN(numericValue)) {
    numericValue = 0;
  }
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
    minimumFractionDigits: 4,
    maximumFractionDigits: 4,
  }).format(numericValue);
}

/**
 * Downloads a file with the specified data, filename, and MIME type.
 *
 * @param data - The data to be downloaded.
 * @param fileName - The name of the file to be downloaded.
 * @param type - The MIME type of the file (e.g., 'application/json', 'text/plain').
 */
export function downloadFile(data: any, fileName: string, type: string): void {
  const fileURL = URL.createObjectURL(new Blob([data], { type }));
  const fileLink = document.createElement("a");
  fileLink.href = fileURL;
  fileLink.setAttribute("download", fileName);
  document.body.appendChild(fileLink);
  fileLink.click();
  document.body.removeChild(fileLink);
  URL.revokeObjectURL(fileURL);
}

export function copyToClipboard(this: any, value: string): void {
  navigator.clipboard.writeText(value);
  this.copied = true;
  setTimeout(() => {
    this.copied = false;
  }, 2000);
}

export function maskBytes(bytes: number, decimals = 2): string {
  if (!+bytes) return "0 Bytes";
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ["Bytes", "KiB", "MiB", "GiB", "TiB", "PiB", "EiB", "ZiB", "YiB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
}

export function maskCep(str: string | null | undefined): string | null | undefined {
  const pattern = "#####-###";
  let i = 0;

  if (!str) {
    return str;
  }

  return pattern.replace(/#/g, () => {
    const char = str.toString()[i++];
    return char ? char : "";
  });
}

export function maskCnpjCpf(value: string | null | undefined): string | null | undefined {
  const cpfPattern = "###.###.###-##";
  const cnpjPattern = "##.###.###/####-##";
  let i = 0;

  if (!value) {
    return value;
  }

  const pattern = value.length > 11 ? cnpjPattern : cpfPattern;

  return pattern.replace(/#/g, () => {
    const char = value.toString()[i++];
    return char ? char : "";
  });
}

export function maskEllipsis(value = "", length = 0): string {
  if (value.length > length) {
    return `${value.substring(0, length)}...`;
  }
  return value;
}

export function maskNumber(value: number | string | null | undefined): string {
  let numericValue: number;
  if (typeof value === "string") {
    numericValue = value.includes(",") ? parseFloat(value.replace(/\./g, "").replace(",", ".")) : Number(value);
  } else {
    numericValue = Number(value);
  }
  if (isNaN(numericValue)) {
    numericValue = 0;
  }
  return new Intl.NumberFormat("pt-BR", {}).format(numericValue);
}

export function maskPercentage(value: number | string): string {
  return (
    new Intl.NumberFormat("pt-BR", { maximumFractionDigits: 2, minimumFractionDigits: 2 }).format(Number(value)) + " %"
  );
}

export function maskPhone(str: string | null | undefined): string | null | undefined {
  const patterns = [
    "+## (##) #####-####",
    "+## (##) ####-####",
    "(##) #####-####",
    "(##) ####-####",
    "#####-####",
    "####-####",
  ];

  if (!str) {
    return str;
  }

  let i = 0;
  const inputDigits = str.replace(/\D/g, ""); // Remove non-digit characters

  const pattern =
    patterns.find((p) => {
      const numDigits = p.replace(/[^#]/g, "").length;
      return inputDigits.length === numDigits;
    }) || patterns[3]; // Default pattern if none is found

  return pattern.replace(/#/g, () => {
    const char = inputDigits[i++];
    return char ? char : "";
  });
}

export function titleCase(value: string | null | undefined): string | null | undefined {
  if (!value) {
    return value;
  }
  return value
    .toString()
    .toLowerCase()
    .split(" ")
    .map((word) => {
      if (word.length < 3) {
        return word;
      }
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(" ");
}

export default {
  adjustToLocalTimezone,
  copyToClipboard,
  downloadFile,
  formatCurrency,
  formatDateTime,
  localDateISO,
  maskBytes,
  maskCep,
  maskCnpjCpf,
  maskDate,
  maskISODate,
  maskDateTime,
  maskEllipsis,
  maskNumber,
  maskPercentage,
  maskPhone,
  resetProperties,
  titleCase,
};
